/**
 * Campus selection persistence layer.
 *
 * Source of truth for a signed-in person is MinistryPlatform
 * (`Contacts.Web_Congregation_ID`, maintained by `/api/set-home-campus`). The
 * cookie is the fast first paint: it lets every page on every
 * woodsidebible.org subdomain render the right campus before the MP round trip
 * comes back, and it is all a signed-out visitor has. When MP and the cookie
 * disagree, MP wins (see useCampusSelection).
 *
 * Cookie: `woodside-selected-campus`, JSON, domain=.woodsidebible.org so
 * woodsidebible.org, apps., events., prayer. and my. all read the same value.
 *
 * Same-page reactivity: CustomEvent('campus-picker:changed') on window so every
 * widget on the page updates without a reload. Other tabs and subdomains pick
 * up the cookie the next time they gain focus.
 *
 * The old site's `tbx-ws__selected-location` cookie (base64) is read once as a
 * migration fallback and never written. Nothing on the new site reads it.
 */

const COOKIE_NAME = 'woodside-selected-campus';
const LEGACY_COOKIE = 'tbx-ws__selected-location';
const COOKIE_DOMAIN = '.woodsidebible.org';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year
export const CHANGE_EVENT = 'campus-picker:changed';
/** Fired with the latest live-status map each time the widget polls Sardius */
export const LIVE_EVENT = 'campus-picker:live';

export interface SelectedCampus {
  congregationId: number;
  name: string;
  /** Two-letter code, when MP has one */
  shortName?: string | null;
  slug: string;
  svgUrl: string | null;
}

// ---------------------------------------------------------------------------
// Read
// ---------------------------------------------------------------------------

/** Read the selected campus. Checks the cookie first, then the legacy cookie. */
export function getSelectedCampus(): SelectedCampus | null {
  if (typeof window === 'undefined') return null;

  const campus = readCookie();
  if (campus) return campus;

  // Migrate from the old site's cookie so returning visitors keep their pick
  const legacy = readLegacyCookie();
  if (legacy) {
    writeCookie(legacy);
    return legacy;
  }

  return null;
}

/** Convenience: just the congregation ID */
export function getSelectedCampusId(): number | null {
  return getSelectedCampus()?.congregationId ?? null;
}

// ---------------------------------------------------------------------------
// Write
// ---------------------------------------------------------------------------

/** Persist campus selection and notify other widgets on the page. */
export function setSelectedCampus(campus: SelectedCampus): void {
  writeCookie(campus);
  dispatchChangeEvent(campus);
}

// ---------------------------------------------------------------------------
// Listen (for other widgets on the same page)
// ---------------------------------------------------------------------------

export type CampusChangeHandler = (campus: SelectedCampus) => void;

/**
 * Subscribe to campus changes on the current page.
 * Returns an unsubscribe function.
 */
export function onCampusChange(handler: CampusChangeHandler): () => void {
  function handleCustom(e: Event) {
    const detail = (e as CustomEvent<SelectedCampus>).detail;
    if (detail) handler(detail);
  }

  window.addEventListener(CHANGE_EVENT, handleCustom);

  return () => {
    window.removeEventListener(CHANGE_EVENT, handleCustom);
  };
}

// ---------------------------------------------------------------------------
// Live status (for the header nav item, which lives outside React)
// ---------------------------------------------------------------------------

export type LiveStatusHandler = (liveIds: Set<number>) => void;

/** Broadcast which Congregation_IDs are streaming right now. */
export function dispatchLiveStatus(liveIds: Set<number>): void {
  window.dispatchEvent(new CustomEvent(LIVE_EVENT, { detail: liveIds }));
}

/** Subscribe to live-status polls on the current page. Returns an unsubscribe. */
export function onLiveStatus(handler: LiveStatusHandler): () => void {
  function handle(e: Event) {
    const detail = (e as CustomEvent<Set<number>>).detail;
    if (detail) handler(detail);
  }
  window.addEventListener(LIVE_EVENT, handle);
  return () => window.removeEventListener(LIVE_EVENT, handle);
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function dispatchChangeEvent(campus: SelectedCampus): void {
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: campus }));
}

/** Determine cookie domain: .woodsidebible.org on prod, omitted on localhost. */
function getCookieDomain(): string {
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return '';
  if (host.endsWith('woodsidebible.org')) return COOKIE_DOMAIN;
  // Other domains (e.g. churchhub.dev): use the root domain
  const parts = host.split('.');
  if (parts.length >= 2) return '.' + parts.slice(-2).join('.');
  return '';
}

function buildCookieString(name: string, value: string): string {
  const domain = getCookieDomain();
  let cookie = `${name}=${value};path=/;max-age=${COOKIE_MAX_AGE};SameSite=Lax`;
  if (domain) cookie += `;domain=${domain}`;
  return cookie;
}

function writeCookie(campus: SelectedCampus): void {
  try {
    document.cookie = buildCookieString(COOKIE_NAME, encodeURIComponent(JSON.stringify(campus)));
  } catch {
    /* cookie blocked */
  }
}

function readRawCookie(name: string): string | null {
  const match = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  if (!match) return null;
  return decodeURIComponent(match.split('=').slice(1).join('='));
}

function readCookie(): SelectedCampus | null {
  try {
    const raw = readRawCookie(COOKIE_NAME);
    if (!raw) return null;
    const data = JSON.parse(raw) as Partial<SelectedCampus>;
    if (!data.congregationId) return null;
    return {
      congregationId: Number(data.congregationId),
      name: data.name ?? '',
      shortName: data.shortName ?? null,
      slug: data.slug ?? '',
      svgUrl: data.svgUrl ?? null,
    };
  } catch {
    return null;
  }
}

/** Read the old site's cookie (migration only). */
function readLegacyCookie(): SelectedCampus | null {
  try {
    const raw = readRawCookie(LEGACY_COOKIE);
    if (!raw) return null;
    const data = JSON.parse(atob(raw));
    if (!data.location_id) return null;
    return {
      congregationId: Number(data.location_id),
      name: data.location_name ?? '',
      slug: data.location_name?.toLowerCase().replace(/\s+/g, '-') ?? '',
      svgUrl: null,
    };
  } catch {
    return null;
  }
}
